import type { BoxProps } from '@chakra-ui/react';
import { Box, Stack, Text } from '@chakra-ui/react';
import { Fragment } from 'react';
import type { PromoCode } from 'types/cart/PromoCode';
import type { ProductComparison } from 'types/cms/blocks';
import type { Table as TableType } from 'types/cms/Table';

import { ProductCTA } from '../ProductCTA';
import { ProductImage } from '../ProductImage';
import { ProductInfo } from '../ProductInfo';
import { TableCell } from './TableCell';
import { TableRow } from './TableRow';

interface TableProps extends BoxProps {
  title: string;
  table: TableType;
  products: ProductComparison['products'];
  promoCode?: PromoCode;
  ctaType: ProductComparison['ctaType'];
  imageType: ProductComparison['imageType'];
}

export function Table({
  title,
  products,
  table,
  promoCode,
  ctaType,
  imageType,
  ...props
}: TableProps) {
  const productsCount = products.length;
  const hasMoreThanFourProducts = productsCount > 4;

  return (
    <Box as="table" w="100%" {...props}>
      <Box as="thead" display="block">
        <TableRow as="tr" productsCount={productsCount} py={0}>
          <Text
            size={{ base: 'h2', lg: 'h3' }}
            as="td"
            textAlign="left"
            display={{
              base: 'none',
              lg: hasMoreThanFourProducts ? 'none' : 'block',
            }}
          >
            {title}
          </Text>
          {products.map((product, index) => (
            <Stack
              key={product.slug}
              as="th"
              textAlign="left"
              gap="2xs"
              gridColumn={{
                base: 'auto',
                lg: index === 0 && !hasMoreThanFourProducts ? 2 : 'auto',
              }}
            >
              <ProductImage
                image={product.image}
                formatThumbnail={product.formatThumbnail}
                shopifyImageUrl={product.shopifyImageUrl}
                href={product.href}
                slug={product.slug}
                imageType={imageType}
                productsCount={productsCount}
              />
              <ProductInfo
                as="span"
                slug={product.slug}
                href={product.href}
                promoCode={promoCode}
                ctaType={ctaType}
              />
            </Stack>
          ))}
        </TableRow>
      </Box>
      <Box as="tbody" display="block">
        {table.rows.map((row) => (
          <TableRow
            key={row._key}
            productsCount={productsCount}
            rowGap="5xs"
            py="3xs"
            isDividerVisible
          >
            {row.cells.slice(1, row.cells.length).map((cell, cellIndex) => {
              const heading = row.cells[0];

              if (cellIndex === 0) {
                return (
                  <Fragment key={cellIndex}>
                    <TableCell
                      as="th"
                      isHeading
                      gridColumn={{
                        base: 1,
                        lg: hasMoreThanFourProducts ? 1 : 'auto',
                      }}
                      gridRow={{
                        base: 1,
                        lg: hasMoreThanFourProducts ? 1 : 'auto',
                      }}
                    >
                      {heading}
                    </TableCell>
                    <TableCell
                      key={`${cellIndex}-cell`}
                      gridColumn={{
                        base: 1,
                        lg: hasMoreThanFourProducts ? 1 : 'auto',
                      }}
                      gridRow={{
                        base: 2,
                        lg: hasMoreThanFourProducts ? 2 : 'auto',
                      }}
                    >
                      {cell}
                    </TableCell>
                  </Fragment>
                );
              }

              return (
                <TableCell
                  key={cellIndex}
                  gridRow={{
                    base: 2,
                    lg: hasMoreThanFourProducts ? 2 : 'auto',
                  }}
                >
                  {cell}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
        {ctaType !== 'none' && (
          <TableRow productsCount={productsCount} pb={0} isDividerVisible>
            {products.map(({ slug, href }, index) => (
              <ProductCTA
                as="td"
                key={slug}
                slug={slug}
                href={href}
                gridColumn={{
                  lg: index === 0 && !hasMoreThanFourProducts ? 2 : 'auto',
                }}
                promoCode={promoCode}
                ctaType={ctaType}
              />
            ))}
          </TableRow>
        )}
      </Box>
    </Box>
  );
}
